import { create } from 'zustand';
import type { Item } from './canvasStore';
import type { AICluster } from '@/lib/spatialClustering';

// History action types
export type HistoryActionType = 
  | 'CREATE_ITEM'
  | 'DELETE_ITEM'
  | 'DELETE_ITEMS'
  | 'MOVE_ITEM'
  | 'MOVE_ITEMS'
  | 'UPDATE_CONTENT'
  | 'MOVE_TO_BOARD'
  | 'GROUP_CLUSTER'
  | 'UNGROUP_CLUSTER'
  // Partnr: everything one assistant message did, undone and redone as one step
  | 'GROUP'
  // Partnr's create_folder: the new folder, plus items it moved in
  | 'CREATE_FOLDER'
  // Partnr's diagram and connection arrows
  | 'CREATE_ARROWS';

export interface HistoryAction {
  type: HistoryActionType;
  timestamp: number;
  // For create/delete
  items?: Item[];
  // For move (position changes)
  itemId?: string;
  previousPositions?: Map<string, { x: number; y: number }>;
  newPositions?: Map<string, { x: number; y: number }>;
  // For content updates
  previousContent?: Record<string, unknown>;
  newContent?: Record<string, unknown>;
  // For board moves
  itemIds?: string[];
  previousBoardId?: string;
  newBoardId?: string;
  // For grouping into (or ungrouping) a cluster: the board's saved clusters and
  // clustering state before and after, alongside the moved positions above
  previousClusters?: AICluster[];
  newClusters?: AICluster[];
  previousClustering?: { enabled: boolean; scope: 'board' | 'manual' };
  newClustering?: { enabled: boolean; scope: 'board' | 'manual' };
  // GROUP: the steps in the order they happened, and the key that collects them
  children?: HistoryAction[];
  groupKey?: string;
  label?: string;
  // CREATE_FOLDER: the folder's board (itemIds and previousBoardId hold what it moved in)
  boardId?: string;
  // CREATE_ARROWS
  arrowIds?: string[];
}

/** The undo group a step belongs to: one Partnr reply. */
export interface HistoryGroup {
  key: string;
  label: string;
}

interface HistoryState {
  undoStack: HistoryAction[];
  redoStack: HistoryAction[];
  maxHistorySize: number;
  
  // Actions
  /**
   * Records a step. With `group`, the step joins the GROUP entry with that
   * key (making it if needed) instead of getting its own entry: Partnr passes
   * the assistant message, so a whole turn undoes as one step even when its
   * actions apply over time. Only callers that pass a group join it, so the
   * user's own edits in the meantime keep their own entries.
   */
  pushAction: (action: HistoryAction, group?: HistoryGroup) => void;
  undo: () => HistoryAction | null;
  redo: () => HistoryAction | null;
  canUndo: () => boolean;
  canRedo: () => boolean;
  clear: () => void;

  /** The GROUP entry for `key` still on the undo stack, or null */
  findGroup: (key: string) => HistoryAction | null;
  /** Removes and returns the GROUP entry for `key` (undoing it out of order); it goes on the redo stack */
  takeGroup: (key: string) => HistoryAction | null;
}

export const useHistoryStore = create<HistoryState>((set, get) => ({
  undoStack: [],
  redoStack: [],
  maxHistorySize: 5, // Strict limit of 5 steps
  
  pushAction: (action, group) => {
    set((state) => {
      if (group) {
        const at = state.undoStack.findIndex((a) => a.type === 'GROUP' && a.groupKey === group.key);
        if (at >= 0) {
          const existing = state.undoStack[at];
          const updated: HistoryAction = { ...existing, timestamp: action.timestamp, children: [...(existing.children ?? []), action] };
          return { undoStack: state.undoStack.map((a, i) => (i === at ? updated : a)), redoStack: [] };
        }
        action = { type: 'GROUP', timestamp: action.timestamp, groupKey: group.key, label: group.label, children: [action] };
      }
      const newStack = [...state.undoStack, action];
      // Keep only the last 5 actions
      while (newStack.length > state.maxHistorySize) {
        newStack.shift();
      }
      return {
        undoStack: newStack,
        redoStack: [], // Clear redo stack on new action
      };
    });
  },
  
  undo: () => {
    const { undoStack } = get();
    if (undoStack.length === 0) return null;
    
    const action = undoStack[undoStack.length - 1];
    
    set((state) => ({
      undoStack: state.undoStack.slice(0, -1),
      redoStack: [...state.redoStack, action],
    }));
    
    return action;
  },
  
  redo: () => {
    const { redoStack } = get();
    if (redoStack.length === 0) return null;
    
    const action = redoStack[redoStack.length - 1];
    
    set((state) => ({
      redoStack: state.redoStack.slice(0, -1),
      undoStack: [...state.undoStack, action],
    }));
    
    return action;
  },
  
  canUndo: () => get().undoStack.length > 0,
  
  canRedo: () => get().redoStack.length > 0,
  
  clear: () => set({ undoStack: [], redoStack: [] }),

  findGroup: (key) => get().undoStack.find((a) => a.type === 'GROUP' && a.groupKey === key) ?? null,

  takeGroup: (key) => {
    const group = get().findGroup(key);
    if (!group) return null;
    set((state) => ({
      undoStack: state.undoStack.filter((a) => a !== group),
      redoStack: [...state.redoStack, group],
    }));
    return group;
  },
}));
